import { ReactNode } from "react";
import { OidcAuthProvider } from "@mesterplan/auth-react";
import { NetClientsProvider } from "@/contexts/NetClientsContext";

/**
 * Top-level OIDC auth wrapper for the .NET-backed code paths. Mount once at
 * the root above the rest of the providers. It still coexists with Firebase
 * Auth during the Phase 4 cutover while the remaining browser login pages are
 * moved over.
 *
 * Config reads from Vite env vars:
 *  - VITE_IDP_ISSUER_URL — e.g. `https://idp.rs-systemer.dk`
 *  - VITE_IDP_CLIENT_ID — defaults to `mesterplan-dev` in dev and `mesterplan` otherwise
 *  - VITE_MESTERPLAN_API_URL — API origin when the web app and API are hosted separately
 *
 * `check-sso` performs a silent browser-session check on mount. If the user is
 * already signed in with the platform identity provider, auth flips to
 * `authenticated` without prompting. Otherwise the app stays in
 * `unauthenticated` state and the existing Firebase Auth path continues to
 * handle sign-in until the Phase 4 auth flip is complete.
 */
export function NetAuthProvider({ children }: { children: ReactNode }) {
  const issuerUrl = import.meta.env.VITE_IDP_ISSUER_URL;
  const clientId = import.meta.env.VITE_IDP_CLIENT_ID ?? (import.meta.env.DEV ? "mesterplan-dev" : "mesterplan");
  const apiBaseUrl = import.meta.env.VITE_MESTERPLAN_API_URL ?? "";

  if (!issuerUrl) {
    // No OIDC issuer configured yet. Keep the Firebase-backed path working for
    // local environments that have not been switched over.
    return <>{children}</>;
  }

  return (
    <OidcAuthProvider config={{ issuerUrl, clientId }} apiBaseUrl={apiBaseUrl}>
      <NetClientsProvider>{children}</NetClientsProvider>
    </OidcAuthProvider>
  );
}
